import './dokumente.css';
import { useState, type FormEvent } from 'react';
import { api, errorText } from '../../api';
import { fmtDate, fmtTime, plural, today } from '../../lib/format';
import { DOC_FOLDERS, folderLabel, guessFolder, type DocFolder } from '../../lib/ordner';
import { useToast } from '../../lib/toast';
import { useApi } from '../../lib/useApi';
import type { DocsTabResponse, KanzleiDoc } from './types';

// Reiter „Hochladen“ beim Mandanten (wie in der Demo): Steuerbescheide und Jahresabschlüsse (FiBu, Admin) und Dokumente
// für die App des Mandanten. Er findet sie unter „Dokumente“ (Dokumente im gewählten Ordner, z. B. „Vollmachten“) bzw.
// „Jahresabschluss“ und bekommt eine Benachrichtigung. BWA und Lohnunterlagen haben eigene Reiter. Ein Dokument kann „zum Unterschreiben“ sein: Der
// Mandant unterschreibt in der App, das unterschriebene PDF steht danach in der Liste.

const TAXES = [
  ['KSt', 'Körperschaftsteuer', 'Körperschaftsteuerbescheid'],
  ['ESt', 'Einkommensteuer', 'Einkommensteuerbescheid'],
  ['GewSt', 'Gewerbesteuer', 'Gewerbesteuerbescheid'],
  ['USt', 'Umsatzsteuer', 'Umsatzsteuerbescheid'],
  ['VZ', 'Vorauszahlungen', 'Vorauszahlungsbescheid'],
  ['sonstige', 'Sonstiger Bescheid', ''],
] as const;
type Tax = (typeof TAXES)[number][0];

// Teile des Jahresabschlusses vom Steuerberater; vorgeschlagen wird die Art aus dem Dateinamen
const JA_PARTS = [
  ['komplett', 'Jahresabschluss komplett'],
  ['bilanz', 'Bilanz'],
  ['guv', 'Gewinn- und Verlustrechnung'],
  ['anhang', 'Anhang'],
  ['euer', 'Einnahmen-Überschuss-Rechnung (EÜR)'],
  ['kst', 'Körperschaftsteuererklärung'],
  ['gewst', 'Gewerbesteuererklärung'],
  ['ust', 'Umsatzsteuererklärung'],
  ['est', 'Einkommensteuererklärung'],
  ['ebilanz', 'E-Bilanz Übermittlungsprotokoll'],
  ['sonstige', 'Sonstiges'],
] as const;
type JaPart = (typeof JA_PARTS)[number][0];
const GUESS: [JaPart, RegExp][] = [
  ['euer', /(^|[^a-z])e(ü|ue)r([^a-z]|$)|(ü|ue)berschuss/i],
  ['ebilanz', /(^|[^a-z])e-?bilanz/i],
  ['bilanz', /bilanz/i],
  ['guv', /guv|gewinn.?und.?verlust/i],
  ['anhang', /anhang/i],
  ['kst', /k(ö|oe)rperschaft|(^|[^a-z])kst/i],
  ['gewst', /gewerbe|gewst/i],
  ['ust', /umsatzsteuer|(^|[^a-z])ust/i],
  ['est', /einkommen|(^|[^a-z])est([^a-z]|$)/i],
  ['komplett', /abschluss/i],
];
const guess = (name: string): JaPart | '' => GUESS.find(([, re]) => re.test(name))?.[0] ?? '';
type JaFile = { file: File; part: JaPart | ''; title: string };
type Kind = 'bescheid' | 'jahresabschluss' | 'dokument';
const KIND_LABEL: Record<Kind, string> = { bescheid: 'Steuerbescheid', jahresabschluss: 'Jahresabschluss', dokument: 'Dokument' };

export function HochladenTab({ clientId }: { clientId: string }) {
  const toast = useToast();
  const res = useApi<DocsTabResponse>(`/api/kanzlei/docs/${clientId}`, { live: ['docs', 'clients'], clientId });
  const t = today();
  const [kind, setKind] = useState<Kind | null>(null);
  const [tax, setTax] = useState<Tax>('KSt');
  const [year, setYear] = useState(t.year - 1);
  const [title, setTitle] = useState('');
  const [sign, setSign] = useState(false);
  // Ordner: aus dem Titel vorgeschlagen, bis die Kanzlei selbst einen wählt
  const [folder, setFolder] = useState<DocFolder | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [jaFiles, setJaFiles] = useState<JaFile[]>([]);
  const [fileKey, setFileKey] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const d = res.data;
  if (!d) return res.error ? <p className="note">{res.error}</p> : <div className="spinner" aria-label="Lädt" />;

  const k = kind ?? (d.bescheide ? 'bescheid' : 'dokument');
  const years = k === 'jahresabschluss' ? [1, 2, 3, 4, 5].map((n) => t.year - n) : [1, 0, -1, -2, -3].map((n) => t.year + n);
  const y = years.includes(year) ? year : t.year - 1;
  const name = TAXES.find((x) => x[0] === tax)![2];
  const f = folder ?? guessFolder(title);
  const shown =
    k === 'dokument'
      ? title.trim() &&
        `${sign ? `Bitte unterschreiben: ${title.trim()}` : title.trim()}${f ? ` · unter Dokumente › ${folderLabel(f)}` : ''}`
      : k === 'jahresabschluss'
        ? jaFiles.length > 0 && `Jahresabschluss ${y} mit ${plural(jaFiles.length, 'Dokument', 'Dokumenten')}`
        : tax === 'sonstige'
          ? title.trim() && (title.includes(String(y)) ? title.trim() : `${title.trim()} ${y}`)
          : `${name} ${y}`;
  const setJa = (i: number, v: Partial<JaFile>) => {
    setJaFiles((l) => l.map((x, j) => (j === i ? { ...x, ...v } : x)));
    setError(null);
  };

  const submitJa = async () => {
    if (!jaFiles.length) return setError('Bitte die Dateien auswählen (PDF oder Foto).');
    if (jaFiles.some((x) => !x.part)) return setError('Bitte bei jeder Datei wählen, was sie ist.');
    if (jaFiles.some((x) => x.part === 'sonstige' && !x.title.trim())) {
      return setError('Bitte bei „Sonstiges“ eintragen, was es ist (z. B. Lagebericht).');
    }
    setError(null);
    setBusy(true);
    const fd = new FormData();
    // zuerst die Angaben (je Datei die Art), dann die Dateien in derselben Reihenfolge
    fd.append(
      'data',
      JSON.stringify({
        year: y,
        files: jaFiles.map((x) => (x.part === 'sonstige' ? { part: x.part, title: x.title.trim() } : { part: x.part })),
      }),
    );
    for (const x of jaFiles) fd.append('file', x.file, x.file.name);
    try {
      const r = await api.post<{ title: string }>(`/api/kanzlei/jahresabschluss/${clientId}`, fd);
      toast(
        `Hochgeladen: ${r.title} (${plural(jaFiles.length, 'Dokument', 'Dokumente')}) · ${d.client.name} sieht es in der App`,
      );
      setJaFiles([]);
      setFileKey((x) => x + 1);
      await res.reload();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (k === 'jahresabschluss') return submitJa();
    if (k === 'dokument' && !title.trim()) return setError('Bitte einen Titel eintragen.');
    if (k === 'dokument' && !f) return setError('Bitte den Ordner wählen.');
    if (k === 'bescheid' && tax === 'sonstige' && !title.trim()) {
      return setError('Bitte eintragen, was für ein Bescheid es ist (z. B. Grundsteuerbescheid).');
    }
    if (!file) return setError('Bitte die Datei auswählen (PDF oder Foto).');
    setError(null);
    setBusy(true);
    const fd = new FormData();
    // zuerst die Angaben, dann die Datei (so prüft der Server, bevor er speichert)
    fd.append(
      'data',
      JSON.stringify(
        k === 'bescheid'
          ? { kind: k, tax, year: y, ...(tax === 'sonstige' ? { title: title.trim() } : {}) }
          : { kind: k, title: title.trim(), folder: f, ...(sign ? { sign: true } : {}) },
      ),
    );
    fd.append('file', file, file.name);
    try {
      const r = await api.post<{ title: string; sign: boolean }>(`/api/kanzlei/docs/${clientId}`, fd);
      toast(
        r.sign
          ? `Hochgeladen: ${r.title} · ${d.client.name} kann es in der App unterschreiben`
          : `Hochgeladen: ${r.title} · ${d.client.name} sieht es in der App`,
      );
      setFile(null);
      setFileKey((x) => x + 1);
      setTitle('');
      setSign(false);
      setFolder(null);
      await res.reload();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (x: KanzleiDoc) => {
    setBusy(true);
    try {
      await api.del(`/api/kanzlei/doc/${x.id}`);
      setRemoving(null);
      await res.reload();
      toast(`Entfernt: ${x.title}`);
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid2 docs-k">
      <div className="card">
        <div className="ch">
          <h3>Für den Mandanten hochladen</h3>
          <span>erscheint sofort in seiner App</span>
        </div>
        <form className="form" onSubmit={submit} noValidate>
          <div className="row docs-two">
            <label>
              Art
              <select
                value={k}
                onChange={(e) => {
                  setKind(e.target.value as Kind);
                  setSign(false);
                  setFolder(null);
                  setFile(null);
                  setJaFiles([]);
                  setError(null);
                }}
              >
                {d.bescheide && <option value="bescheid">Steuerbescheid</option>}
                {d.bescheide && <option value="jahresabschluss">Jahresabschluss</option>}
                <option value="dokument">Dokument</option>
              </select>
            </label>
            {k === 'dokument' && (
              <label>
                Ordner beim Mandanten *
                <select
                  value={f}
                  onChange={(e) => {
                    setFolder(e.target.value as DocFolder);
                    setError(null);
                  }}
                >
                  <option value="">Bitte wählen</option>
                  {DOC_FOLDERS.map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {k !== 'dokument' && (
              <label>
                {k === 'jahresabschluss' ? 'Geschäftsjahr' : 'Jahr'}
                <select value={y} onChange={(e) => setYear(Number(e.target.value))}>
                  {years.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
          {k === 'bescheid' && (
            <label>
              Art des Bescheids
              <select
                value={tax}
                onChange={(e) => {
                  setTax(e.target.value as Tax);
                  setError(null);
                }}
              >
                {TAXES.map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
            </label>
          )}
          {(k === 'dokument' || (k === 'bescheid' && tax === 'sonstige')) && (
            <label>
              {k === 'dokument' ? 'Titel *' : 'Was für ein Bescheid? *'}
              <input
                type="text"
                value={title}
                maxLength={120}
                placeholder={
                  k === 'dokument' ? (sign ? 'z. B. Vollmacht' : 'z. B. Handelsregisterauszug') : 'z. B. Grundsteuerbescheid'
                }
                onChange={(e) => {
                  setTitle(e.target.value);
                  setError(null);
                }}
              />
            </label>
          )}
          {k === 'jahresabschluss' ? (
            <>
              <label>
                Dateien (PDF oder Foto, mehrere möglich) *
                <input
                  key={`ja-${fileKey}`}
                  type="file"
                  multiple
                  accept="application/pdf,image/*"
                  onChange={(e) => {
                    const l = [...(e.target.files ?? [])].slice(0, 20);
                    setJaFiles(l.map((f) => ({ file: f, part: guess(f.name), title: '' })));
                    setError(null);
                  }}
                />
              </label>
              {jaFiles.length > 0 && (
                <div className="docs-ja">
                  {jaFiles.map((x, i) => (
                    <div key={i} className="docs-ja-r">
                      <span className="docs-ja-n">📄 {x.file.name}</span>
                      <select
                        aria-label={`Was ist ${x.file.name}?`}
                        value={x.part}
                        onChange={(e) => setJa(i, { part: e.target.value as JaPart })}
                      >
                        <option value="">Bitte wählen</option>
                        {JA_PARTS.map(([v, l]) => (
                          <option key={v} value={v}>
                            {l}
                          </option>
                        ))}
                      </select>
                      {x.part === 'sonstige' && (
                        <input
                          type="text"
                          value={x.title}
                          maxLength={120}
                          placeholder="z. B. Lagebericht"
                          aria-label={`Bezeichnung für ${x.file.name}`}
                          onChange={(e) => setJa(i, { title: e.target.value })}
                        />
                      )}
                    </div>
                  ))}
                  <p className="note">Die Art ist aus dem Dateinamen vorgeschlagen, bitte kurz prüfen.</p>
                </div>
              )}
            </>
          ) : (
            <label>
              Datei (PDF oder Foto) *
              <input
                key={`${k}-${fileKey}`}
                type="file"
                accept={sign ? 'application/pdf,image/jpeg,image/png' : 'application/pdf,image/*'}
                onChange={(e) => {
                  setFile(e.target.files?.[0] ?? null);
                  setError(null);
                }}
              />
            </label>
          )}
          {k === 'dokument' && (
            <label className="docs-sign">
              <input
                type="checkbox"
                checked={sign}
                onChange={(e) => {
                  setSign(e.target.checked);
                  setError(null);
                }}
              />
              <span>
                <b>Der Mandant soll es unterschreiben</b>
                <small>
                  Er unterschreibt in der App mit dem Finger, das unterschriebene PDF steht danach hier in der Liste. Für
                  Vollmacht, Auftrag, AV-Vertrag; nicht für Kündigungen von Arbeitsverträgen (die müssen auf Papier sein).
                </small>
              </span>
            </label>
          )}
          {shown && <p className="note">Der Mandant sieht: {shown}</p>}
          {error && (
            <p className="note docs-err" role="alert">
              {error}
            </p>
          )}
          <div>
            <button type="submit" className="btn dark" disabled={busy}>
              Hochladen
            </button>
          </div>
          <p className="note">
            {d.client.name} findet es in der App unter „Dokumente“, den Jahresabschluss auch unter „Jahresabschluss“, und bekommt
            eine Benachrichtigung. Die BWA kommt in den Reiter „BWA“, Lohnunterlagen in den Reiter „Lohn“.
          </p>
        </form>
      </div>
      <div className="card">
        <div className="ch">
          <h3>Zuletzt hochgeladen</h3>
          <span>{d.docs.length}</span>
        </div>
        {d.docs.length === 0 ? (
          <p className="note">Noch nichts hochgeladen.</p>
        ) : (
          <div className="docs-list">
            {d.docs.map((x) => (
              <div key={x.id} className="docs-item">
                {removing === x.id ? (
                  <div className="docs-ask">
                    <span>{x.title} entfernen? Der Mandant sieht es dann nicht mehr.</span>
                    <span className="docs-btns">
                      <button type="button" className="btn sm dark" disabled={busy} onClick={() => remove(x)}>
                        Ja, entfernen
                      </button>
                      <button type="button" className="btn sm" onClick={() => setRemoving(null)}>
                        Abbrechen
                      </button>
                    </span>
                  </div>
                ) : (
                  <>
                    <span className="docs-tx">
                      <a href={`/api/kanzlei/doc/${x.id}/file`} target="_blank" rel="noopener noreferrer">
                        {x.title}
                      </a>
                      <span className="sub">
                        {x.kind === 'dokument' ? `Dokumente › ${folderLabel(x.folder)}` : KIND_LABEL[x.kind]} · {x.file.name} ·{' '}
                        {fmtDate(x.uploadedAt)}
                        {x.uploadedBy ? ` · ${x.uploadedBy}` : ''}
                      </span>
                      {x.sign?.signedAt && (
                        <span className="docs-signed">
                          ✍️ Unterschrieben von {x.sign.signerName} am {fmtDate(x.sign.signedAt)}, {fmtTime(x.sign.signedAt)} Uhr
                          ·{' '}
                          <a href={`/api/kanzlei/doc/${x.id}/signed`} target="_blank" rel="noopener noreferrer">
                            Unterschriebenes PDF
                          </a>
                        </span>
                      )}
                    </span>
                    <span className="docs-act">
                      {x.sign ? (
                        x.sign.signedAt ? (
                          <span className="ok">✓ unterschrieben</span>
                        ) : (
                          <span className="wait">wartet auf Unterschrift</span>
                        )
                      ) : (
                        <span className="ok">✓ beim Mandanten</span>
                      )}
                      <button type="button" className="del" onClick={() => setRemoving(x.id)} aria-label={`${x.title} entfernen`}>
                        Entfernen
                      </button>
                    </span>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
